import { siloMarketsV3, siloV3LenderKey } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import type {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'

/**
 * Silo v3 on-chain oracle-price fetcher.
 *
 * Mirror of `siloV2.ts` — v3 shares the same ISiloOracle interface
 * (`quote(uint256, address)` / `quoteToken()`) as v2, so the on-chain code
 * paths are byte-identical except for which registry map (`siloMarketsV3`)
 * and which lender-key helper (`siloV3LenderKey`) we call. Keeping the two
 * separate files means v3 gets its own multicall batch, its own output
 * bucket (`targetLender` = `SILO_V3_<HEX>`), and doesn't poison v2
 * diagnostics when only one version is failing.
 */
const SiloOracleAbi = [
  {
    inputs: [
      { name: '_baseAmount', type: 'uint256' },
      { name: '_baseToken', type: 'address' },
    ],
    name: 'quote',
    outputs: [{ name: 'quoteAmount', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'quoteToken',
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

interface SiloV3OracleMeta {
  entries: {
    token: string
    decimals: number
    silo: string
    lenderKey: string
    oracle: string
    partnerToken: string
    isStatic: boolean
  }[]
  callIndexMap: { [key: string]: number }
  quoteTokenIndexMap: { [key: string]: number }
}

function lookupUSD(context: ParseContext, asset: string): number {
  const lc = asset.toLowerCase()
  const groupKey =
    context.tokenList?.[lc]?.assetGroup ?? `${context.chainId}-${lc}`
  return context.usdPrices[groupKey] ?? context.usdPrices[lc] ?? 0
}

export function getSiloV3Calls(
  chainId: string,
): FetcherResult<SiloV3OracleMeta>[] {
  const allMarkets = siloMarketsV3()?.[chainId]
  if (!allMarkets || allMarkets.length === 0) return []

  const tokensWithRealOracle = new Set<string>()
  for (const market of allMarkets) {
    for (const half of [market.silo0, market.silo1]) {
      if (half.solvencyOracle && half.solvencyOracle !== ZERO_ADDRESS) {
        tokensWithRealOracle.add(half.token.toLowerCase())
      }
    }
  }

  const calls: MulticallCall[] = []
  const callIndexMap: { [key: string]: number } = {}
  const quoteTokenIndexMap: { [key: string]: number } = {}
  const entries: SiloV3OracleMeta['entries'] = []

  for (const market of allMarkets) {
    const lenderKey = siloV3LenderKey(market.siloConfig)
    const halves = [market.silo0, market.silo1] as const
    const isStatic = halves.map(
      (h) => !h.solvencyOracle || h.solvencyOracle === ZERO_ADDRESS,
    )

    if (isStatic[0] && isStatic[1]) {
      const t0 = halves[0].token.toLowerCase()
      const t1 = halves[1].token.toLowerCase()
      if (!tokensWithRealOracle.has(t0) && !tokensWithRealOracle.has(t1)) {
        continue
      }
    }

    for (let i = 0; i < 2; i++) {
      const half = halves[i]
      const partner = halves[1 - i]
      const tokenLc = half.token.toLowerCase()
      const partnerLc = partner.token.toLowerCase()

      if (isStatic[i]) {
        entries.push({
          token: tokenLc,
          decimals: half.decimals,
          silo: half.silo.toLowerCase(),
          lenderKey,
          oracle: ZERO_ADDRESS,
          partnerToken: partnerLc,
          isStatic: true,
        })
        continue
      }

      const oracleLc = half.solvencyOracle.toLowerCase()

      entries.push({
        token: tokenLc,
        decimals: half.decimals,
        silo: half.silo.toLowerCase(),
        lenderKey,
        oracle: oracleLc,
        partnerToken: partnerLc,
        isStatic: false,
      })

      const quoteKey = `${oracleLc}:${tokenLc}`
      if (!(quoteKey in callIndexMap)) {
        callIndexMap[quoteKey] = calls.length
        calls.push({
          address: half.solvencyOracle,
          name: 'quote',
          params: [BigInt(10) ** BigInt(half.decimals), half.token],
        })
      }

      if (!(oracleLc in quoteTokenIndexMap)) {
        quoteTokenIndexMap[oracleLc] = calls.length
        calls.push({
          address: half.solvencyOracle,
          name: 'quoteToken',
          params: [],
        })
      }
    }
  }

  if (entries.length === 0) return []

  return [
    {
      calls,
      meta: { entries, callIndexMap, quoteTokenIndexMap },
      lender: 'SILO_V3',
    },
  ]
}

export function parseSiloV3Results(
  data: any[],
  meta: SiloV3OracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const entries: OraclePriceEntry[] = []
  const seen = new Set<string>()

  for (const entry of meta.entries) {
    const dedupKey = `${entry.lenderKey}:${entry.token}`
    if (seen.has(dedupKey)) continue

    try {
      let priceUSD = 0
      let rawPrice = 1

      if (entry.isStatic) {
        const ownUSD = lookupUSD(context, entry.token)
        const partnerUSD = lookupUSD(context, entry.partnerToken)
        priceUSD = ownUSD || partnerUSD
        if (!priceUSD) continue
        rawPrice = 1
      } else {
        const quoteCallIdx =
          meta.callIndexMap[`${entry.oracle}:${entry.token}`]
        if (quoteCallIdx === undefined) continue
        const rawQuote = data[quoteCallIdx]
        if (!rawQuote || rawQuote === '0x') continue

        const quoteAmount = BigInt(rawQuote.toString())
        if (quoteAmount === 0n) continue

        const qtIdx = meta.quoteTokenIndexMap[entry.oracle]
        if (qtIdx === undefined) continue
        const rawQt = data[qtIdx]
        if (!rawQt || rawQt === '0x') continue

        const quoteTokenAddr = (rawQt as string).toLowerCase()

        // Silo oracles return WAD-normalized values: quote(10^baseDecimals,
        // base) = priceRatio × 1e18 regardless of the quote token's
        // decimals. Dividing by qtDecimals would overscale by
        // 10^(18 - qtDecimals) — for USDC that's 1e12.
        const priceInQt = Number(quoteAmount) / 1e18
        if (isNaN(priceInQt) || priceInQt === 0) continue

        const qtUSD = lookupUSD(context, quoteTokenAddr)
        if (!qtUSD) continue

        rawPrice = priceInQt
        priceUSD = priceInQt * qtUSD
      }

      if (isNaN(priceUSD) || priceUSD === 0) continue

      entries.push({
        asset: entry.token,
        price: rawPrice,
        priceUSD,
        marketUid: createMarketUid(
          context.chainId,
          entry.lenderKey,
          entry.silo,
        ),
        targetLender: entry.lenderKey,
        ...(entry.isStatic
          ? { staticBase: true, baseAsset: entry.token }
          : {}),
      })
      seen.add(dedupKey)
    } catch {
      // skip invalid results
    }
  }

  return entries
}

export function getSiloV3Abi() {
  return SiloOracleAbi
}

export const siloV3Fetcher = {
  getCalls: getSiloV3Calls,
  parse: parseSiloV3Results,
  getAbi: getSiloV3Abi,
}
